import { Home, LogOut, Settings, ShieldCheck, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useAuth } from "@/stores/auth";

export function UserMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const initials =
    (user.name ?? user.email)
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "U";

  const handleSignOut = async () => {
    await signOut();
    toast.success("Você saiu.");
    navigate("/login", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 gap-2 pl-1.5 pr-2"
          aria-label={`Menu do usuário ${user.email}`}
        >
          <Avatar src={user.picture_url} initials={initials} />
          <span className="hidden md:inline text-sm font-medium max-w-[140px] truncate">
            {user.name ?? user.email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-3 py-2">
          <Avatar src={user.picture_url} initials={initials} className="h-10 w-10" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{user.name ?? user.email}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            <RoleBadge role={user.role} />
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/")}>
          <Home className="h-4 w-4" aria-hidden /> Início
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/profile")}>
          <UserIcon className="h-4 w-4" aria-hidden /> Meu perfil
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/settings")}>
          <Settings className="h-4 w-4" aria-hidden /> Configurações
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void handleSignOut();
          }}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4" aria-hidden /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Avatar({
  src,
  initials,
  className,
}: {
  src: string | null;
  initials: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-900 text-xs font-semibold ring-1 ring-primary-200 overflow-hidden",
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  if (role === "super_admin") {
    return (
      <span className="mt-0.5 inline-flex items-center gap-1 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-900">
        <ShieldCheck className="h-2.5 w-2.5" aria-hidden /> super admin
      </span>
    );
  }
  return (
    <span className="mt-0.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {role}
    </span>
  );
}
